import React, { useEffect, useRef, useState } from 'react';
import { useChannelStateContext, useChatContext } from 'stream-chat-react';

import { Box, Button, Typography } from '@mui/material';

import highlightTextToHtml from '@common/modules/Chat/utils/highlightTextToHtml';

async function jumpToMessage(channel: any, messageId: string) {
	await channel.state.loadMessageIntoState(messageId);

	requestAnimationFrame(() => {
		const el = document.querySelector(`[data-message-id="${messageId}"]`) as HTMLElement | null;
		if (!el) return;

		el.scrollIntoView({ behavior: 'smooth', block: 'center' });
		el.style.outline = '1px solid rgba(59,130,246,0.4)';

		setTimeout(() => {
			el.style.outline = '';
		}, 1200);
	});
}

// TODO refactor
function htmlToText(html: string) {
	const div = document.createElement('div');
	div.innerHTML = html;
	return div.textContent || '';
}

function normalizeText(text: string) {
	return text
		.replace(/\u00a0/g, ' ')
		.replace(/\s+/g, ' ')
		.trim()
		.toLowerCase();
}

function getSearchTokens(query: string) {
	return normalizeText(query).split(' ').filter(Boolean);
}

function filterMessagesByTokens(messages: any[], tokens: string[]) {
	if (!tokens.length) return [];

	return messages.filter((m: any) => {
		const messageText = normalizeText(htmlToText(m.text ?? ''));
		return tokens.every((token) => messageText.includes(token));
	});
}

function PinnedMessagePanel() {
	const { channel, pinnedMessages } = useChannelStateContext();
	const { client } = useChatContext();

	const [activePinnedIndex, setActivePinnedIndex] = useState(0);
	const hasPinned = !!pinnedMessages?.length;
	const activePinned = pinnedMessages?.[activePinnedIndex];

	const [query, setQuery] = useState('');
	const [results, setResults] = useState<any[]>([]);
	const [isSearching, setIsSearching] = useState(false);

	const debounceRef = useRef<number | null>(null);
	const inputRef = useRef<HTMLInputElement>(null);

	const onPinnedClick = async () => {
		if (!activePinned?.id) return;

		await jumpToMessage(channel, activePinned.id);
		setActivePinnedIndex((i) => (i + 1) % pinnedMessages.length);
	};

	useEffect(() => {
		const tokens = getSearchTokens(query);
		if (!tokens.length) {
			setResults([]);
			setIsSearching(false);
			return;
		}

		if (debounceRef.current) {
			clearTimeout(debounceRef.current);
		}

		debounceRef.current = window.setTimeout(async () => {
			setIsSearching(true);

			try {
				const searchTerm = tokens.reduce(
					(longest, token) => (token.length > longest.length ? token : longest),
					tokens[0],
				);

				const res = channel?.search
					? await channel.search(searchTerm, { limit: 20 })
					: await client.search({ cid: channel?.cid }, searchTerm, { limit: 20 });

				const messages = filterMessagesByTokens(
					res.results.map((r: any) => r.message).filter(Boolean),
					tokens,
				);

				if (!messages.length && channel?.state?.messages?.length) {
					setResults(filterMessagesByTokens(channel.state.messages, tokens));
					return;
				}

				setResults(messages);
			} catch (error) {
				if (channel?.state?.messages?.length) {
					setResults(filterMessagesByTokens(channel.state.messages, tokens));
				} else {
					setResults([]);
				}
				// eslint-disable-next-line no-console
				console.error('Chat search failed', error);
			} finally {
				setIsSearching(false);
			}
		}, 300);

		return () => {
			if (debounceRef.current) clearTimeout(debounceRef.current);
		};
	}, [query, client, channel?.cid]);

	const clearSearch = () => {
		setQuery('');
		setResults([]);
		setIsSearching(false);
		requestAnimationFrame(() => inputRef.current?.focus());
	};

	return (
		<Box
			sx={{
				borderBottom: '1px solid #e5e7eb',
				background: '#fafafa',
				padding: '8px 0px',
				minWidth: 0,
				alignSelf: 'stretch',
				width: '100%',
			}}
		>
			<Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
				<input
					ref={inputRef}
					value={query}
					onChange={(e) => setQuery(e.target.value)}
					placeholder="Search in chat…"
					style={{
						flex: 1,
						padding: '7px 10px',
						border: '1px solid #d1d5db',
						borderRadius: 8,
						fontSize: 14,
					}}
				/>

				{query && (
					<Button
						onClick={clearSearch}
						variant="text"
						sx={{ fontSize: '14px', textTransform: 'none' }}
					>
						Clear search
					</Button>
				)}
			</Box>

			{hasPinned && (
				<Box
					role="button"
					onClick={onPinnedClick}
					sx={{
						cursor: 'pointer',
						padding: '6px 8px',
						borderRadius: 1,
						background: '#fff',
						border: '1px solid #e5e7eb',
						mb: 1,
					}}
				>
					<Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
						<Typography fontSize={12} fontWeight={600}>
							📌 Pinned message
						</Typography>

						{pinnedMessages.length > 1 && (
							<Typography fontSize={12} color="text.secondary">
								{activePinnedIndex + 1}/{pinnedMessages.length}
							</Typography>
						)}
					</Box>

					<Box
						sx={{
							minWidth: 0,
							maxWidth: '100%',
							fontSize: 13,
							color: '#374151',
							overflow: 'hidden',
							whiteSpace: 'nowrap',
							textOverflow: 'ellipsis',
						}}
					>
						{htmlToText(activePinned?.text ?? '').trim()}
					</Box>
				</Box>
			)}

			<Box>
				{isSearching && (
					<Typography fontSize={12} color="text.secondary">
						Searching…
					</Typography>
				)}

				{!isSearching && query && results.length === 0 && (
					<Typography fontSize={12} color="text.secondary">
						No results
					</Typography>
				)}

				{results.map((m) => (
					<Box
						key={m.id}
						role="button"
						onClick={() => jumpToMessage(channel, m.id)}
						sx={{
							cursor: 'pointer',
							padding: '6px 0',
							fontSize: 13,
							borderTop: '1px solid rgba(229,231,235,0.7)',
						}}
						dangerouslySetInnerHTML={{
							__html: `<strong>${m.user?.name ?? ''}:</strong> ${highlightTextToHtml(
								htmlToText(m.text ?? ''),
								query,
							)}`,
						}}
					/>
				))}
			</Box>
		</Box>
	);
}

export default PinnedMessagePanel;
